import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const JUDETE = [
  'Alba','Arad','Argeș','Bacău','Bihor','Bistrița-Năsăud','Botoșani','Brăila','Brașov',
  'București','Buzău','Călărași','Caraș-Severin','Cluj','Constanța','Covasna','Dâmbovița',
  'Dolj','Galați','Giurgiu','Gorj','Harghita','Hunedoara','Ialomița','Iași','Ilfov',
  'Maramureș','Mehedinți','Mureș','Neamț','Olt','Prahova','Sălaj','Satu Mare','Sibiu',
  'Suceava','Teleorman','Timiș','Tulcea','Vâlcea','Vaslui','Vrancea',
];

const css = `
  * { box-sizing: border-box; }
  .cp-page { min-height: 100vh; background: #f5f5f7; display: flex; align-items: flex-start; justify-content: center; padding: 24px 16px; }
  .cp-card { background: #fff; border-radius: 16px; padding: 40px; width: 100%; max-width: 560px; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
  .cp-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .cp-fld { display: flex; flex-direction: column; gap: 5px; }
  .cp-lbl { font-size: 13px; font-weight: 500; color: #374151; }
  .cp-inp { padding: 11px 14px; font-size: 15px; border: 1.5px solid #e5e7eb; border-radius: 10px; outline: none; font-family: inherit; width: 100%; -webkit-appearance: none; }
  .cp-inp:focus { border-color: #2d2d7e; }
  .cp-inp.err { border-color: #ef4444; }
  .cp-err { font-size: 12px; color: #ef4444; }
  .cp-btn { padding: 14px; font-size: 16px; font-weight: 600; background: #2d2d7e; color: #fff; border: none; border-radius: 10px; cursor: pointer; width: 100%; }
  .cp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .cp-section { font-size: 12px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.8px; margin: 4px 0 -4px; }
  @media (max-width: 599px) {
    .cp-page { padding: 0; }
    .cp-card { border-radius: 0; padding: 24px 16px; min-height: 100vh; box-shadow: none; }
    .cp-row2 { grid-template-columns: 1fr; }
  }
`;

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const tip = user?.tip_persoana || 'fizica';

  const [form, setForm]         = useState({});
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [cuiLoading, setCuiLoading] = useState(false);
  const [cuiMsg, setCuiMsg]         = useState(null); // { ok: bool, text: string }
  const [cuiGasit, setCuiGasit]     = useState(false); // câmpurile ANAF sunt locked

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = (k) => ({
    className: `cp-inp${errors[k] ? ' err' : ''}`,
    value: form[k] || '',
    onChange: e => set(k, e.target.value),
  });
  // Câmp needitabil (preluat din ANAF)
  const roInp = (k) => ({
    className: 'cp-inp',
    value: form[k] || '',
    readOnly: cuiGasit,
    style: cuiGasit ? { backgroundColor: '#f9fafb', color: '#6b7280', cursor: 'default' } : {},
    onChange: e => !cuiGasit && set(k, e.target.value),
  });

  function validCNP(cnp) {
    if (!cnp || !/^\d{13}$/.test(cnp)) return false;

    // Cifra 1: 1-9 (nu 0)
    const s = parseInt(cnp[0]);
    if (s === 0) return false;

    // Luna naștere: 01-12
    const luna = parseInt(cnp.substring(3, 5));
    if (luna < 1 || luna > 12) return false;

    // An naștere (pentru validare zi bisect)
    const yy = parseInt(cnp.substring(1, 3));
    let an;
    if (s <= 2) an = 1900 + yy;
    else if (s <= 4) an = 1800 + yy;
    else an = 2000 + yy;

    // Zi naștere: validă pentru luna și an
    const zi = parseInt(cnp.substring(5, 7));
    if (zi < 1 || zi > 31) return false;
    const bisect = an % 4 === 0 && (an % 100 !== 0 || an % 400 === 0);
    const zileMax = [0,31,bisect?29:28,31,30,31,30,31,31,30,31,30,31];
    if (zi > zileMax[luna]) return false;

    // Cod județ: 01-46, 51, 52
    const jud = parseInt(cnp.substring(7, 9));
    if (!((jud >= 1 && jud <= 46) || jud === 51 || jud === 52)) return false;

    // Număr ordine: 001-999
    const nr = parseInt(cnp.substring(9, 12));
    if (nr < 1 || nr > 999) return false;

    // Cifra de control
    const weights = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
    const sum = weights.reduce((acc, w, i) => acc + w * parseInt(cnp[i]), 0);
    const remainder = sum % 11;
    const control = remainder === 10 ? 1 : remainder;
    return control === parseInt(cnp[12]);
  }

  function validateFizica() {
    const e = {};
    if (!form.prenume || form.prenume.trim().length < 2)             e.prenume  = 'Minim 2 caractere';
    if (!form.nume    || form.nume.trim().length < 2)                e.nume     = 'Minim 2 caractere';
    if (!form.cnp     || !validCNP(form.cnp.trim()))                 e.cnp      = 'CNP invalid — verificați cifra de control';
    if (!form.serie_ci || !/^[A-Z]{2}$/.test(form.serie_ci.trim())) e.serie_ci = '2 litere majuscule (ex: AZ)';
    if (!form.numar_ci || !/^\d{6}$/.test(form.numar_ci.trim()))    e.numar_ci = 'Exact 6 cifre';
    if (!form.adresa  || form.adresa.trim().length < 5)              e.adresa   = 'Adresă incompletă';
    if (!form.localitate || form.localitate.trim().length < 2)       e.localitate = 'Obligatorie';
    if (!form.judet)                                                 e.judet    = 'Selectați județul';
    return e;
  }

  function validateJuridica() {
    const e = {};
    if (!form.denumire_firma || form.denumire_firma.trim().length < 2) e.denumire_firma = 'Obligatorie';
    if (!form.cui            || form.cui.trim().length < 4)            e.cui            = 'CUI invalid';
    if (!form.adresa_sediu   || form.adresa_sediu.trim().length < 5)   e.adresa_sediu   = 'Adresă incompletă';
    if (!form.reprezentant_legal || form.reprezentant_legal.trim().length < 3) e.reprezentant_legal = 'Obligatoriu';
    if (!form.cnp_reprezentant || !validCNP(form.cnp_reprezentant.trim())) e.cnp_reprezentant = 'CNP invalid — verificați cifra de control';
    return e;
  }

  async function cautaCUI() {
    const cui = (form.cui || '').replace(/[^0-9]/g, '');
    if (cui.length < 4) { setCuiMsg({ ok: false, text: 'Introduceți minim 4 cifre' }); return; }
    setCuiLoading(true);
    setCuiMsg(null);
    try {
      // Apel prin Vercel function /api/cui — face server-to-server VIES (fără CORS, fără blocare IP)
      const resp = await fetch(`/api/cui?cui=${cui}`);
      const json = await resp.json();

      if (!resp.ok || !json.success) {
        setCuiMsg({ ok: false, text: json.message || 'CUI negăsit — completați manual datele' });
        return;
      }

      const d = json.data;
      setForm(prev => ({
        ...prev,
        cui:            d.cui,
        denumire_firma: d.denumire_firma,
        adresa_sediu:   d.adresa_sediu,
        nr_reg_com:     d.nr_reg_com || prev.nr_reg_com || '',
        judet:          d.judet || prev.judet || '',
      }));
      setCuiGasit(true);
      setCuiMsg({ ok: true, text: `✓ ${d.denumire_firma}${d.scpTVA ? ' · Plătitor TVA' : ''}` });
      setErrors(e => ({ ...e, cui: undefined, denumire_firma: undefined, adresa_sediu: undefined }));
    } catch (err) {
      setCuiMsg({ ok: false, text: 'Eroare conexiune. Completați manual datele firmei.' });
    } finally {
      setCuiLoading(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errs = tip === 'fizica' ? validateFizica() : validateJuridica();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await api.post('/auth/complete-profile', { ...form, tip_persoana: tip });
      updateUser(res.data.user);
      toast.success('Profil completat! Bun venit!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Eroare la salvare');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{css}</style>
      <div className="cp-page">
        <div className="cp-card">
          <div style={{ fontSize: '36px', textAlign: 'center', marginBottom: '12px' }}>
            {tip === 'fizica' ? '👤' : '🏢'}
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px', textAlign: 'center' }}>
            Completați datele dvs.
          </h1>
          <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', margin: '0 0 6px' }}>
            {tip === 'fizica' ? 'Persoană fizică' : 'Persoană juridică'}
          </p>
          <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', margin: '0 0 28px', lineHeight: '1.5' }}>
            Datele sunt necesare pentru procesarea cererilor conform legislației în vigoare.
          </p>

          <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {tip === 'fizica' ? (
              <>
                <p className="cp-section">Date personale</p>
                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">Prenume *</label>
                    <input {...inp('prenume')} placeholder="Ion" autoFocus />
                    {errors.prenume && <span className="cp-err">{errors.prenume}</span>}
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">Nume *</label>
                    <input {...inp('nume')} placeholder="Popescu" />
                    {errors.nume && <span className="cp-err">{errors.nume}</span>}
                  </div>
                </div>

                <div className="cp-fld">
                  <label className="cp-lbl">CNP *</label>
                  <input {...inp('cnp')} placeholder="1234567890123" maxLength={13} inputMode="numeric" />
                  {errors.cnp && <span className="cp-err">{errors.cnp}</span>}
                </div>

                <p className="cp-section">Act de identitate</p>
                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">Serie CI * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(2 litere)</span></label>
                    <input {...inp('serie_ci')} placeholder="AZ" maxLength={2}
                      style={{ textTransform: 'uppercase' }}
                      onChange={e => set('serie_ci', e.target.value.toUpperCase())} />
                    {errors.serie_ci && <span className="cp-err">{errors.serie_ci}</span>}
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">Număr CI * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(6 cifre)</span></label>
                    <input {...inp('numar_ci')} placeholder="123456" maxLength={6} inputMode="numeric" />
                    {errors.numar_ci && <span className="cp-err">{errors.numar_ci}</span>}
                  </div>
                </div>

                <p className="cp-section">Adresă domiciliu</p>
                <div className="cp-fld">
                  <label className="cp-lbl">Stradă, număr, bloc, etc. *</label>
                  <input {...inp('adresa')} placeholder="Str. Exemplu nr. 1, bl. A, ap. 5" />
                  {errors.adresa && <span className="cp-err">{errors.adresa}</span>}
                </div>

                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">Localitate *</label>
                    <input {...inp('localitate')} placeholder="București" />
                    {errors.localitate && <span className="cp-err">{errors.localitate}</span>}
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">Județ *</label>
                    <select className={`cp-inp${errors.judet ? ' err' : ''}`}
                      value={form.judet || ''} onChange={e => set('judet', e.target.value)}>
                      <option value="">Selectați...</option>
                      {JUDETE.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                    {errors.judet && <span className="cp-err">{errors.judet}</span>}
                  </div>
                </div>

                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">Cod poștal</label>
                    <input {...inp('cod_postal')} placeholder="012345" maxLength={6} inputMode="numeric" />
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">Telefon</label>
                    <input {...inp('phone')} type="tel" placeholder="07xx xxx xxx" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="cp-section">Căutare firmă după CUI</p>

                {/* CUI search */}
                <div className="cp-fld">
                  <label className="cp-lbl">CUI / CIF *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      {...inp('cui')}
                      placeholder="ex: 12345678"
                      inputMode="numeric"
                      autoFocus
                      style={{ flex: 1 }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); cautaCUI(); } }}
                      onChange={e => { set('cui', e.target.value); setCuiMsg(null); setCuiGasit(false); }}
                    />
                    <button
                      type="button"
                      onClick={cautaCUI}
                      disabled={cuiLoading}
                      style={{ padding: '0 18px', background: cuiLoading ? '#e5e7eb' : '#2d2d7e', color: cuiLoading ? '#888' : '#fff', border: 'none', borderRadius: '10px', cursor: cuiLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}
                    >
                      {cuiLoading ? '⏳' : '🔍 Caută'}
                    </button>
                  </div>
                  {errors.cui && <span className="cp-err">{errors.cui}</span>}
                  {cuiMsg && (
                    <span style={{ fontSize: '12px', color: cuiMsg.ok ? '#059669' : '#ef4444', marginTop: '2px', display: 'block' }}>
                      {cuiMsg.text}
                    </span>
                  )}
                </div>

                <p className="cp-section">Date firmă / organizație</p>

                <div className="cp-fld">
                  <label className="cp-lbl">
                    Denumire firmă / ONG *
                    {cuiGasit && <span style={{ fontSize: '11px', color: '#059669', marginLeft: '6px', fontWeight: 400 }}>🔒 din ANAF</span>}
                  </label>
                  <input {...roInp('denumire_firma')} placeholder="Se completează automat după căutare" />
                  {errors.denumire_firma && <span className="cp-err">{errors.denumire_firma}</span>}
                </div>

                <div className="cp-fld">
                  <label className="cp-lbl">
                    Adresă sediu social *
                    {cuiGasit && <span style={{ fontSize: '11px', color: '#059669', marginLeft: '6px', fontWeight: 400 }}>🔒 din ANAF</span>}
                  </label>
                  <input {...roInp('adresa_sediu')} placeholder="Se completează automat după căutare" />
                  {errors.adresa_sediu && <span className="cp-err">{errors.adresa_sediu}</span>}
                </div>

                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">
                      Nr. Reg. Comerțului
                      {cuiGasit && form.nr_reg_com && <span style={{ fontSize: '11px', color: '#059669', marginLeft: '6px', fontWeight: 400 }}>🔒</span>}
                    </label>
                    <input {...roInp('nr_reg_com')} placeholder="J40/1234/2020" />
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">
                      Județ
                      {cuiGasit && form.judet && <span style={{ fontSize: '11px', color: '#059669', marginLeft: '6px', fontWeight: 400 }}>🔒</span>}
                    </label>
                    <input {...roInp('judet')} placeholder="Se completează automat" />
                  </div>
                </div>

                <p className="cp-section">Reprezentant legal</p>
                <div className="cp-fld">
                  <label className="cp-lbl">Nume complet reprezentant legal *</label>
                  <input {...inp('reprezentant_legal')} placeholder="Ion Popescu" />
                  {errors.reprezentant_legal && <span className="cp-err">{errors.reprezentant_legal}</span>}
                </div>

                <div className="cp-row2">
                  <div className="cp-fld">
                    <label className="cp-lbl">CNP reprezentant *</label>
                    <input {...inp('cnp_reprezentant')} placeholder="1234567890123" maxLength={13} inputMode="numeric" />
                    {errors.cnp_reprezentant && <span className="cp-err">{errors.cnp_reprezentant}</span>}
                  </div>
                  <div className="cp-fld">
                    <label className="cp-lbl">Funcție</label>
                    <input {...inp('functie_reprezentant')} placeholder="Administrator" />
                  </div>
                </div>

                <div className="cp-fld">
                  <label className="cp-lbl">Telefon contact</label>
                  <input {...inp('phone')} type="tel" placeholder="07xx xxx xxx" />
                </div>
              </>
            )}

            <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#1d4ed8', lineHeight: '1.5', marginTop: '4px' }}>
              🔒 Datele dvs. sunt protejate conform GDPR și sunt utilizate exclusiv pentru procesarea cererilor adresate primăriei.
            </div>

            <button type="submit" disabled={loading} className="cp-btn">
              {loading ? 'Se salvează...' : 'Finalizează înregistrarea →'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
